import {
  Block,
  RootBlock,
  isRootBlock,
  BlockRelationship,
  commonBlocksCompoundIndexes,
  CommonBlocksCompoundIndexes,
  getOnChangeHandler,
  getChildrenKeys,
  isBRMoreImportant,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { TreeManager, getTreeManager } from '@basehub/utils/tree/index'
import { useBlocksMapStore } from './blocks-map-store'
import { rIC } from '~/lib/request-idle-callback'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { ExperimentalNoIndexDiff } from 'replicache'
import { createStore } from 'zustand'
import { beginPerfReport } from '@basehub/utils/perf/index'

export type WorkingTreeProps = {
  rootBlockId: string | null
  tree: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  lastRevertedAt: string | null
  blockRelationshipsMap: Map<string, BlockRelationship>
  blockRelationshipsByParentId: Map<string, Set<string>>
  treeRef: React.MutableRefObject<TreeManager<
    Block,
    CommonBlocksCompoundIndexes
  > | null>
  recentlyCreatedBlocks: Set<string>
  removeRecentlyCreatedBlock: (blockId: string) => void
}

export const createWorkingTreeStore = (
  initProps?: Partial<WorkingTreeProps>
) => {
  const DEFAULT_PROPS: WorkingTreeProps = {
    rootBlockId: null,
    tree: null,
    lastRevertedAt: null,
    blockRelationshipsMap: new Map(),
    blockRelationshipsByParentId: new Map(),
    treeRef: {
      current: null,
    },
    recentlyCreatedBlocks: new Set(),
    removeRecentlyCreatedBlock: (_blockId: string) => {
      throw new Error('removeRecentlyCreatedBlock not implemented')
    },
  }
  return createStore<WorkingTreeProps>()((set, get) => {
    return {
      ...DEFAULT_PROPS,
      ...initProps,
      removeRecentlyCreatedBlock: (blockId: string) => {
        const newSet = new Set(get().recentlyCreatedBlocks)
        newSet.delete(blockId)
        set({ recentlyCreatedBlocks: newSet })
      },
    }
  })
}

export type WorkingTreeStore = ReturnType<typeof createWorkingTreeStore>

export function handleDiff(
  diff: ExperimentalNoIndexDiff,
  {
    rootBlockId,
    state: oldState,
    logger,
  }: {
    rootBlockId: string
    state: WorkingTreeProps
    logger: (message: unknown) => void
  }
): null | Partial<WorkingTreeProps> {
  logger(diff)
  try {
    const perfReporter = beginPerfReport('working-tree-diff')
    let newTree = oldState.tree
    const bStore = useBlocksMapStore.getState()
    const recentlyDeleted = bStore.recentlyDeleted
    const blocksMap = bStore.blocksMap
    const brsMap = bStore.brsMap
    const recentlyCreatedBlocks = new Set(oldState.recentlyCreatedBlocks)
    let recentlyCreatedBlocksChanged = false

    perfReporter.checkpoint('get current state')

    // deferred as it needs to be done in the second pass
    const minisearchIndexDeferredWork: Array<() => void> = []

    /**
     * Update tree. Never re-create the whole thing (unless it doesn't exist yet).
     * 1. add — should add node under parent
     * 2. del — should remove node from parent
     * 3. change — tougher:
     * 3.1. get parent and make sure it's under it (cause block may have been moved). then overwrite.
     * 3.2. if parent is null, then it's a root block. overwrite.
     * 3.3. updates should update sortKey and sort it through the flat tree i guess.
     */
    if (!newTree) {
      let rootBlock: RootBlock | null = null
      for (const diffOp of diff) {
        if (diffOp.key.startsWith('br/')) {
          const br = (
            'newValue' in diffOp ? diffOp.newValue : diffOp.oldValue
          ) as BlockRelationship

          if (diffOp.op === 'del') {
            brsMap.delete(br.id)
          } else {
            /** We keep BRs even if they have deletedAt, as they may act as lineage overrides. */
            brsMap.set(br.id, br)
          }
        } else if (diffOp.key.startsWith('block/')) {
          const block = (
            'newValue' in diffOp ? diffOp.newValue : diffOp.oldValue
          ) as Block

          if (block.deletedAt || diffOp.op === 'del') {
            blocksMap.delete(block.id)
            if (block.deletedAt) {
              recentlyDeleted.set(block.id, block.deletedAt)
            }
          } else {
            blocksMap.set(block.id, block)
            if (block.id === oldState.rootBlockId && isRootBlock(block)) {
              rootBlock = block
              // it's like a cache warmup
              bStore.getBrsByRootId(rootBlock)
            }
          }
        }
      }

      if (!rootBlock) {
        logger(`rootBlock ${rootBlockId} not found`)
        // not necessarily an error, eg when the user switches branches and the root hasn't been pulled yet
        return null
      }

      // clear for discard changes to work correctly
      bStore._brsByRootId.delete(rootBlock.id)
      const blockRelationshipsMap = bStore.getBrsByRootId(rootBlock)

      newTree = getTreeManager<Block, CommonBlocksCompoundIndexes>({
        rootKey: rootBlock.id,
        getValue: (key) => {
          const block = blocksMap.get(key)
          invariant(block, `block ${key} not found in cache`)
          return block
        },
        getChildrenKeys: (_key, value) => {
          const childrenKeys = getChildrenKeys({
            block: value,
            blockRelationshipsMap,
            blocksMap,
            rootBlockId: rootBlock.id,
          })
          return childrenKeys
        },
        getOrder: (key) => {
          const br = blockRelationshipsMap.get(key)
          invariant(br, `block relationship ${key} not found in cache`)
          return br.order
        },
        getKey: (item) => item.id,
        indexKeys: ['sourceBlockId'],
        autoFallbackWithKeys: ['sourceBlockId'],
        compoundIndexes: commonBlocksCompoundIndexes,
        onChange: (props) => {
          const handler = getOnChangeHandler({
            blockRelationshipsMap,
            blocksMap,
          })

          handler(props)
        },
      })

      perfReporter.checkpoint('recreated tree from scratch')
    } else {
      let _rootBlockOld: RootBlock | null = null
      let rootBlock: RootBlock | null = null

      const deferredUpdates: Array<() => void> = []
      const miniSearchIdsToRevalidate = new Set<string>()

      for (const diffOp of diff) {
        if (diffOp.key === `block/${rootBlockId}`) {
          invariant(
            diffOp.op !== 'del',
            `cannot delete active root block ${rootBlockId}`
          )
          rootBlock = diffOp.newValue as RootBlock
          if (diffOp.op === 'change') {
            _rootBlockOld = diffOp.oldValue as RootBlock
          }
        }
        if (diffOp.key.startsWith('block/')) {
          const block = (
            'newValue' in diffOp ? diffOp.newValue : diffOp.oldValue
          ) as Block

          if (block.deletedAt || diffOp.op === 'del') {
            blocksMap.delete(block.id)
            if (block.deletedAt) {
              recentlyDeleted.set(block.id, block.deletedAt)
            }
            continue
          }

          blocksMap.set(block.id, block)
          /**
           * When going over raw blocks, we'll just handle updates here.
           * We'll handle deletions and additions when going over BRs.
           * Think of it like this: BRs give us the tree structure, and blocks give us the actual data.
           */
          const existing = newTree.getNode(block.id, {
            fallbackKey: false,
          })
          if (existing) {
            newTree.updateNode(block.id, { value: block })
            miniSearchIdsToRevalidate.add(block.id)
          }
        }
      }

      if (!rootBlock) {
        rootBlock = bStore.blocksMap.get(rootBlockId) as RootBlock | null
      }
      if (!rootBlock) return null

      if (
        _rootBlockOld &&
        _rootBlockOld.lastRevertedAt !== rootBlock.lastRevertedAt
      ) {
        /**
         * this root was reverted with discard changes. this diff shouldn't happen,
         * as the store will now reset and a new tree will be built. we stop here to prevent further errors.
         */
        return null
      }

      const blockRelationshipsMap = bStore.getBrsByRootId(rootBlock)
      const preventDeferredUpdates = new Set<string>()
      const resolveParentBlock = (
        parentSourceBlockId: string
      ): Block | null => {
        invariant(newTree, 'newTree should be defined by this point')

        const parentBr = blockRelationshipsMap.get(parentSourceBlockId)
        if (parentBr) {
          return (
            newTree.getNode(parentBr.blockId, { fallbackKey: false })?.value ??
            blocksMap.get(parentBr.blockId) ??
            null
          )
        }

        return (
          newTree.getNode(parentSourceBlockId, {
            fallbackKey: 'sourceBlockId',
            allowLinearSearch: true,
          })?.value ??
          blocksMap.get(parentSourceBlockId) ??
          null
        )
      }

      for (const diffOp of diff) {
        if (diffOp.key.startsWith('br/')) {
          const br = (
            'newValue' in diffOp ? diffOp.newValue : diffOp.oldValue
          ) as BlockRelationship

          const alreadyExists = blockRelationshipsMap.getBy(
            'sourceBlockId',
            br.sourceBlockId
          )
          if (
            alreadyExists &&
            (alreadyExists.id !== br.id || alreadyExists.blockId !== br.blockId)
          ) {
            preventDeferredUpdates.delete(alreadyExists.id)
            preventDeferredUpdates.delete(br.id)
            if (
              isBRMoreImportant(br, alreadyExists, rootBlock) ||
              alreadyExists.id === br.id
            ) {
              if (br.sourceBlockId === idToSourceBlockId(rootBlock)) {
                if (
                  br.blockId !== rootBlock.id &&
                  alreadyExists.blockId === rootBlock.id
                ) {
                  // eslint-disable-next-line no-console
                  console.log({
                    br,
                    alreadyExists,
                    rootBlock,
                  })
                  throw new Error(`this shouldn't happen. bug!`)
                }
              }

              /**
               * this will happen after CoW, where we create a new BR. this should have more "priority" as its closer in the lineage.
               *
               * we'll delete the existing BR from the root's BRsMap, and also from the treeManager
               * then we'll resume operations/add it back if necessary.
               */
              recentlyDeleted.set(
                alreadyExists.blockId,
                new Date().toISOString()
              )
              preventDeferredUpdates.add(alreadyExists.id)
              blockRelationshipsMap.delete(alreadyExists.id)
              newTree.deleteNode(alreadyExists.blockId)
              miniSearchIdsToRevalidate.add(alreadyExists.blockId)
            }
          }

          const isPrevInTree = blockRelationshipsMap.has(br.id)

          if (diffOp.op === 'del') {
            brsMap.delete(br.id)
          } else {
            /** We keep BRs even if they have deletedAt, as they may act as lineage overrides. */
            brsMap.set(br.id, br)
          }

          const isNowInTree = blockRelationshipsMap.has(br.id)

          if (!isPrevInTree && !isNowInTree) {
            /**
             * not in this tree, we can ignore.
             * "what about BRs that are removed from the tree via a filter of rootIds?"
             * well, that should only happen via CoW, and in that case, that logic is handled above (`alreadyExists` thing).
             */
            continue
          }

          const isDeletedFromTree =
            diffOp.op === 'del' ||
            br.deletedAt ||
            br.placeholderSince ||
            !isNowInTree

          const existingNode = newTree.getNode(br.blockId, {
            fallbackKey: false,
          })

          if (existingNode) {
            if (isDeletedFromTree) {
              newTree.deleteNode(existingNode.key)
              miniSearchIdsToRevalidate.add(existingNode.value.id)
            } else {
              // if so, we'll just make sure it's under the right parent
              const existingParent = existingNode.parentKey
                ? newTree.getNode(existingNode.parentKey)
                : null
              if (
                existingNode.value.type !== 'root' &&
                (!existingParent ||
                  br.parentId !== idToSourceBlockId(existingParent.value))
              ) {
                deferredUpdates.push(() => {
                  if (preventDeferredUpdates.has(br.id)) return
                  invariant(newTree, 'newTree should be defined by this point')
                  const parent = resolveParentBlock(br.parentId)
                  if (!parent) {
                    console.log({
                      br,
                      parentSourceBlockId: br.parentId,
                      existingParent,
                      existingNode,
                    })
                  }
                  if (!parent) return
                  newTree.updateNode(existingNode.key, {
                    parentKey: parent.id,
                  })
                  miniSearchIdsToRevalidate.add(existingNode.value.id)
                })
              }
              // also handle reorders
              if (existingNode.order !== br.order) {
                newTree.updateNode(existingNode.key, {
                  order: br.order,
                })
                // no need to minisearch revalidate, order doesn't matter for search
              }
            }
          } else {
            if (!isDeletedFromTree) {
              // if not, we'll try to find the block and we'll add it to the tree
              deferredUpdates.push(() => {
                if (preventDeferredUpdates.has(br.id)) return
                invariant(newTree, 'newTree should be defined by this point')
                const child = blocksMap.get(br.blockId)
                const parent = resolveParentBlock(br.parentId)
                if (!child) {
                  console.error({
                    rootBlock,
                    br,
                    parentSourceBlockId: br.parentId,
                  })
                  return // for now, ignore this error
                }
                invariant(child, `child with id ${br.blockId} not found`)
                if (!parent) {
                  logger({
                    br,
                    parentSourceBlockId: br.parentId,
                    blocksMap,
                    existingNode,
                    child,
                  })
                }
                if (!parent) return

                const existing = newTree.getNode(child.id)
                if (existing) {
                  newTree.updateNode(child.id, {
                    value: child,
                    parentKey: parent.id,
                  })
                } else {
                  newTree.addNode(child, { parentKey: parent.id })
                }
                recentlyCreatedBlocks.add(idToSourceBlockId(child))
                recentlyCreatedBlocksChanged = true
                miniSearchIdsToRevalidate.add(child.id)
                newTree.getChildrenKeys(child.id).forEach((childKey) => {
                  if (!newTree) return
                  // update its parentKey -- as it get out of sync after CoW
                  const childNode = newTree.getNode(childKey)
                  if (childNode && childNode.parentKey !== child.id) {
                    newTree.updateNode(childKey, { parentKey: child.id })
                  }
                })
              })
            }
          }
        }
      }

      deferredUpdates.forEach((update) => update())

      perfReporter.checkpoint('updated map')
    }

    /**
     * miniSearch index deferred work
     */
    minisearchIndexDeferredWork.forEach((work) => {
      rIC(() => work())
    })
    perfReporter.checkpoint('updated minisearch')

    oldState.treeRef.current = newTree

    const report = perfReporter.end()
    logger(report)

    return {
      tree: newTree,
      ...(recentlyCreatedBlocksChanged ? { recentlyCreatedBlocks } : {}),
    }
  } catch (error) {
    console.error('uncaught error in provider')
    console.error(error)
    throw error
  }
}
